/**
 * The book.
 *
 * A bookie — any admin — posts lines, twelve friends take them, the bookie
 * confirms the Venmo landed and later settles. The screen is built around the
 * one question a player actually asks, which is never "what are the odds" but
 * "if I put fifty on this, what comes back". So a stake is chosen ONCE at the
 * top and every line on the board is quoted in those terms; the American number
 * is still shown, because it is the price and people argue about prices, but it
 * is never the only number on the row.
 *
 * The other thing the layout has to make obvious is that placing a bet does not
 * make it live. Money moves in Venmo, outside this app, and a slip is a claim
 * until the bookie says otherwise — so 'pending' is never styled as success and
 * the bookie's confirm queue is a first-class section rather than a detail.
 *
 * Every color here is a token. The palette is themeable now, and a hardcoded
 * hex would be the one thing on the screen that ignores the theme.
 */

import { useCallback, useEffect, useState } from 'react'
import type { Tournament } from '../engine/types'
import {
  formatAmerican,
  formatMoney,
  impliedProbability,
  payoutCents,
  profitCents,
  type MarketResult,
} from '../engine/odds'
import { playerName } from '../select'
import { oddsText } from '../share'
import { FieldBoard } from './FieldBoard'
import {
  archiveOdds,
  clearBook,
  fetchBook,
  placeBet,
  postOdds,
  updateBet,
  updateOdds,
  useStore,
  type Bet,
  type Book,
  type OddsMarket,
} from '../store'
import { Action } from './Action'
import { Parlay } from './Parlay'
import { Model } from './Model'
import { Bar, Chip, Empty, Row, SectionRule } from '../ui'
import { BookPosition } from './BookPosition'
import { QuoteField } from './QuoteField'

/**
 * The stakes people actually bet on this trip. Offered as taps rather than a
 * number pad because this is used one-handed, walking, and because a free-text
 * amount is how a $500 typo happens.
 */
/*
 * Three presets, not five.
 *
 * $5 and $10 were never the bet anybody was making in this group, and they cost
 * two of the five slots on a 393px row — which is what forced the custom amount
 * onto a line of its own. Three buttons and the field fit together, so the whole
 * control is one row and the free-text box is a peer of the presets rather than
 * an afterthought below them.
 */
const STAKES = [2500, 5000, 10000]

const STATUS_LABEL: Record<Bet['status'], string> = {
  pending: 'Awaiting Venmo',
  confirmed: 'Live',
  won: 'Won',
  lost: 'Lost',
  void: 'Void',
}

export function Odds({ doc }: { doc: Tournament }) {
  const session = useStore((s) => s.session)
  const me = session?.playerId ?? ''
  /*
   * The bookie's controls, for the bookie.
   *
   * This was `session.isAdmin`, so making somebody the bookie meant handing him
   * the draw, the handicaps, the payouts and the reset button too. He is named
   * in the settings now and gets this screen and nothing else; an admin can
   * still do it, and a document naming nobody behaves as it always did. The
   * server enforces the same rule — see `mayBook` — so this is only about what
   * is worth putting on screen.
   */
  const isAdmin = (session?.isAdmin ?? false) || (me !== '' && doc.settings.bookie === me)

  const [book, setBook] = useState<Book | null>(null)
  const [stake, setStake] = useState(2500)
  const [expanded, setExpanded] = useState<string | null>(null)
  const [tab, setTab] = useState<'board' | 'mine' | 'action' | 'model'>('board')
  /** Which shelf of props is showing — see the chips under the heading. */
  const [propKind, setPropKind] = useState('All')
  const [note, setNote] = useState<string | null>(null)
  const [quoting, setQuoting] = useState(false)
  /*
   * Who the next slip is written for.
   *
   * Bets are called across a table, so the bookie needs to book one in another
   * man's name. It is sticky rather than per-tap because they arrive in runs —
   * three men shout at once and the bookie works down the list.
   */
  const [bettor, setBettor] = useState('')
  const [busy, setBusy] = useState(false)

  const refresh = useCallback(async () => {
    try {
      setBook(await fetchBook())
    } catch (e) {
      setNote(e instanceof Error ? e.message : 'Could not reach the book')
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  /**
   * Every mutation goes through here so that all three of them behave the same:
   * lock the buttons, refetch on success, and say something specific on failure.
   *
   * Refetching rather than patching local state is deliberate. The bookie is
   * moving lines from his own phone, and an optimistic update would show a
   * player a bet placed at a price that no longer exists — the exact confusion
   * `odds_at_placement` exists to prevent. The server's answer is the book.
   */
  const run = async (action: () => Promise<unknown>, ok: string) => {
    setBusy(true)
    try {
      await action()
      await refresh()
      setNote(ok)
    } catch (e) {
      setNote(e instanceof Error ? e.message : 'That did not go through')
    } finally {
      setBusy(false)
      setTimeout(() => setNote(null), 4000)
    }
  }

  const markets = book?.odds ?? []
  const bets = book?.bets ?? []
  const myBets = bets.filter((b) => b.player_id === me)
  /*
   * Lines this man posted and then left alone. Counted on the Mine chip so
   * the reminder finds him wherever he is on this screen — a ticket built
   * on the plane and never backed is the commonest way money fails to get
   * on the board.
   */
  const myUnbacked = markets.filter(
    (m) => m.posted_by === me && m.status === 'open' && !myBets.some((b) => b.odds_id === m.id),
  ).length
  const pending = bets.filter((b) => b.status === 'pending')

  // Open first — the only ones that can still be bet — then closed, then the
  // settled ones, which are history and belong at the bottom.
  const rank = (m: OddsMarket) => (m.status === 'open' ? 0 : m.status === 'closed' ? 1 : 2)
  /*
   * A proposal is not a market yet. It stays off the board and off the
   * shelves — its whole life happens in the approval banner above the tabs
   * — so nobody tries to back a line the bookie has not agreed to write.
   */
  const proposed = markets.filter((m) => m.status === 'proposed')
  // Outrights live in the field table; this list is the one-offs.
  const props = [...markets]
    .filter(
      (m) =>
        m.status !== 'proposed' &&
        m.market !== 'Rhetorical' &&
        !(m.market === 'outright' && m.subject_id),
    )
    .sort((a, b) => rank(a) - rank(b))
  /*
   * The rhetoricals: Perucca's group-chat lines, on the board because that is
   * where the week's needling belongs, unbettable because the man himself
   * ruled "they are rhetorical" — and because none of them could settle
   * without a fistfight, which is the point of them.
   */
  const rhetoricals = markets.filter((m) => m.market === 'Rhetorical')
  /*
   * The shelves, in the order the board grew them, and the shelf on show.
   * A kind with a subject on it — "Net birdies" — is a whole field priced
   * one man at a time, which is exactly the case that needs its own shelf.
   */
  const propKinds = [...new Set(props.map((m) => m.market))].filter(Boolean)
  const shownProps =
    propKind === 'All' ? props : props.filter((m) => m.market === propKind)

  return (
    <>
      <Bar
        kicker="The book"
        title="Odds"
        sub={
          markets.length === 0
            ? 'Nothing posted yet'
            : `${markets.filter((m) => m.status === 'open').length} open · quoted at ${formatMoney(stake)}`
        }
      />

      {note && <div className="bg-signal text-paper px-4 py-2 bc-label">{note}</div>}

      {/*
        A slip that has sat unconfirmed for a day is a problem, not a queue —
        Scott's ruling. It is counted everywhere the action is shown, but the
        money has not moved, and the longer it sits the more likely somebody
        has simply forgotten. The bookie is told how many and asked to clear
        them; a player is told about his own, by name, so he can go and send
        it rather than wonder.
      */}
      {(() => {
        const DAY = 24 * 60 * 60 * 1000
        const stale = bets.filter(
          (b) => b.status === 'pending' && Date.now() - Date.parse(b.placed_at) > DAY,
        )
        if (stale.length === 0) return null
        const mine = stale.filter((b) => b.player_id === me)
        /*
         * A player is only ever told about his OWN stale slips. The else
         * branch below names everybody who owes, which is the bookie's
         * business — it was reaching non-admins whose own slate was clean,
         * seen live on a phone: "Nothing has landed for Babcock."
         */
        if (!isAdmin && mine.length === 0) return null
        const days = (iso: string) =>
          Math.max(1, Math.round((Date.now() - Date.parse(iso)) / DAY))
        return (
          <div className="bg-signal text-paper px-4 py-2.5">
            <p className="bc-label">
              {isAdmin
                ? `${stale.length} slip${stale.length === 1 ? '' : 's'} waiting more than a day`
                : 'You owe the bookie'}
            </p>
            <p className="text-[13px] leading-snug mt-1">
              {mine.length > 0 ? (
                <>
                  {/*
                    One number, because one Venmo settles it. The list of
                    individual slips was the man's own bet history read back
                    to him; what he needs is the figure to type.
                  */}
                  <b className="bc-num">
                    {formatMoney(mine.reduce((n, b) => n + b.stake_cents, 0))}
                  </b>{' '}
                  across {mine.length} slip{mine.length === 1 ? '' : 's'}, the oldest{' '}
                  {days(mine.reduce((o, b) => (b.placed_at < o.placed_at ? b : o)).placed_at)} day
                  {days(mine.reduce((o, b) => (b.placed_at < o.placed_at ? b : o)).placed_at) === 1
                    ? ''
                    : 's'}{' '}
                  ago. Send it to {doc.settings.venmoHandle || 'the bookie'} in one go and he
                  will mark them in.
                </>
              ) : (
                <>
                  Nothing has landed for{' '}
                  {stale
                    .map((b) => playerName(doc, b.player_id))
                    .filter((v, i, a) => a.indexOf(v) === i)
                    .join(', ')}
                  . Confirm them once the money is in, or void them.
                </>
              )}
            </p>
          </div>
        )
      })()}

      {/*
        Pending odds approval.

        The bookie sees every ask with the two answers a bookie has; the man
        who asked sees that his slip is on the desk rather than lost. Nobody
        else sees anything — a declined parlay should vanish without twelve
        men having watched it get declined.
      */}
      {(() => {
        const shown = isAdmin ? proposed : proposed.filter((m) => m.posted_by === me)
        if (shown.length === 0) return null
        return (
          <div className="bg-pop text-paper px-4 py-2.5">
            <p className="bc-label">Pending odds approval</p>
            {shown.map((m) => (
              <div key={m.id} className="mt-1.5">
                <p className="text-[13px] leading-snug">
                  <b className="bc-name">{m.description}</b>{' '}
                  <span className="bc-num">{formatAmerican(m.american_odds)}</span>
                  {isAdmin && <> — {playerName(doc, m.posted_by)} asks</>}
                </p>
                {isAdmin ? (
                  <div className="flex gap-2 mt-1.5">
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() =>
                        run(() => updateOdds(m.id, { status: 'open' }), 'On the board')
                      }
                      className="bc-tap bc-label px-3 bg-paper text-pop disabled:opacity-40"
                    >
                      Book it
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => run(() => archiveOdds(m.id), 'Passed — it never happened')}
                      className="bc-tap bc-label px-3 border border-paper disabled:opacity-40"
                    >
                      Pass
                    </button>
                  </div>
                ) : (
                  <p className="text-[12px] opacity-80 mt-0.5">
                    With {playerName(doc, doc.settings.bookie ?? '') || 'the bookie'} now — it
                    goes live when he books it.
                  </p>
                )}
              </div>
            ))}
          </div>
        )
      })()}

      {/*
        Four now, so the labels shorten: at 390px a fourth column leaves about
        95px, and "The board" at 0.2em tracking does not fit in it.
      */}
      <div className="grid grid-cols-4 border-b border-rule">
        {(['board', 'mine', 'action', 'model'] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`bc-label py-3 bc-tap ${
              tab === t ? 'bg-ink text-paper' : 'bg-paper text-mute'
            }`}
          >
            {t === 'board'
              ? 'Board'
              : t === 'model'
                ? 'Model'
                : t === 'action'
                  ? 'Action'
                  : `Mine${
                      myBets.length + myUnbacked ? ` (${myBets.length + myUnbacked})` : ''
                    }`}
          </button>
        ))}
      </div>

      {tab === 'model' ? (
        <Model doc={doc} book={book} mayBook={isAdmin} onPosted={refresh} />
      ) : tab === 'action' ? (
        book ? <Action doc={doc} book={book} me={me} /> : null
      ) : tab === 'board' ? (
        <>
          <StakePicker stake={stake} onStake={setStake} />

          {/*
            The ticket builder, above the field: it is the one thing on this
            screen that is not already priced, and it prices itself.
          */}
          <Parlay doc={doc} stake={stake} mayBook={isAdmin} onPosted={refresh} />

          {/*
            The field first, then everything that is not a player.
            
            An outright on each man is the spine of the book and belongs in one
            sorted table; a prop like "any eagle Wednesday" is a one-off and
            reads better as its own row. Splitting them stopped the board being
            a list in whatever order the bookie happened to type.
          */}
          {isAdmin && (
            <div className="px-4 py-2.5 border-b border-rule flex items-center gap-2">
              <span className="bc-label text-mute shrink-0">Slip for</span>
              <select
                value={bettor}
                onChange={(e) => setBettor(e.target.value)}
                aria-label="Who this slip is for"
                className="flex-1 min-w-0 border border-ink px-2 py-2 bc-name bg-paper bc-tap"
              >
                <option value="">Me ({playerName(doc, me)})</option>
                {doc.players
                  .filter((p) => p.active && p.id !== me)
                  .map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.displayName}
                    </option>
                  ))}
              </select>
            </div>
          )}

          {book && (
            <FieldBoard
              doc={doc}
              book={book}
              isAdmin={isAdmin}
              onQuote={() => setQuoting(true)}
              onArchive={
                isAdmin
                  ? (id) => run(() => archiveOdds(id), 'Archived — off the board')
                  : undefined
              }
            >
              {(m) => (
                <div className="mt-2">
                  <button
                    type="button"
                    disabled={busy || m.status !== 'open'}
                    onClick={() =>
                      run(
                        () => placeBet(m.id, stake, bettor || undefined),
                        bettor
                          ? `${formatMoney(stake)} on ${playerName(doc, bettor)}'s slip — confirm it when the money lands`
                          : `${formatMoney(stake)} down — send it to the bookie to make it live`,
                      )
                    }
                    className="bc-tap w-full bg-signal text-paper bc-label py-3 disabled:opacity-40"
                  >
                    {m.status !== 'open'
                      ? 'Closed'
                      : `Back him — ${formatMoney(stake)} to win ${formatMoney(profitCents(stake, m.american_odds))}`}
                  </button>
                  {/*
                    One man, one price, a different amount.
                    
                    The board stake is for comparing lines; this is for the bet
                    somebody actually wants to make — the man you fancy gets a
                    hundred while everything else is being read at twenty-five.
                    It leaves the board's own quote alone.
                  */}
                  {m.status === 'open' && (
                    <CustomStake
                      busy={busy}
                      odds={m.american_odds}
                      onPlace={(cents) =>
                        run(
                          () => placeBet(m.id, cents, bettor || undefined),
                          bettor
                            ? `${formatMoney(cents)} on ${playerName(doc, bettor)}'s slip — confirm it when the money lands`
                            : `${formatMoney(cents)} down — send it to the bookie to make it live`,
                        )
                      }
                    />
                  )}
                  {myBets
                    .filter((b) => b.odds_id === m.id)
                    .map((b) => (
                      <p key={b.id} className="text-[11px] text-mute mt-1.5">
                        You have {formatMoney(b.stake_cents)} on him at{' '}
                        {formatAmerican(b.odds_at_placement)} — {b.status}
                      </p>
                    ))}
                </div>
              )}
            </FieldBoard>
          )}

          {isAdmin && quoting && book && (
            <QuoteField
              doc={doc}
              book={book}
              onDone={() => {
                setQuoting(false)
                void refresh()
              }}
            />
          )}

          {/*
            Props get their own roof — Scott's call after a hand-typed line
            appeared jammed under the twelfth outright, truncated to
            "Springer to finish for first…". The field is one table with one
            shape; anything else is a different question and reads as one.
          */}
          {props.length > 0 && <SectionRule>Prop bets</SectionRule>}
          {/*
            And their own shelves. Thirty lines under one heading is a wall,
            so the KINDS the bookie already types — "Net birdies", "Low
            round", "Quota sweep" — become the filter. Derived rather than
            configured: a kind he invents on a tee shows up here by existing.
          */}
          {propKinds.length > 1 && (
            <div className="flex gap-2 px-4 py-2 overflow-x-auto border-b border-rule">
              {['All', ...propKinds].map((k) => (
                <button
                  key={k}
                  type="button"
                  aria-pressed={propKind === k}
                  onClick={() => setPropKind(k)}
                  className={`bc-tap bc-label px-3 shrink-0 border ${
                    propKind === k ? 'bg-ink text-paper border-ink' : 'border-rule bg-paper'
                  }`}
                >
                  {k}
                </button>
              ))}
            </div>
          )}
          {shownProps.length === 0 ? null : (
            shownProps.map((m) => (
              <MarketRow
                key={m.id}
                doc={doc}
                market={m}
                stake={stake}
                busy={busy}
                isAdmin={isAdmin}
                myBets={myBets.filter((b) => b.odds_id === m.id)}
                open={expanded === m.id}
                onToggle={() => setExpanded(expanded === m.id ? null : m.id)}
                onPlace={() =>
                  run(
                    () => placeBet(m.id, stake),
                    `${formatMoney(stake)} down — send it to the bookie to make it live`,
                  )
                }
                onMarket={(patch, ok) => run(() => updateOdds(m.id, patch), ok)}
                onArchive={() => run(() => archiveOdds(m.id), 'Archived — off the board')}
              />
            ))
          )}

          {rhetoricals.length > 0 && (
            <>
              <SectionRule>The rhetoricals</SectionRule>
              <p className="px-4 pt-1 pb-2 text-[13px] text-mute leading-snug">
                From the group chat, preserved as posted. No prices, no slips —
                the author has ruled them rhetorical, and nothing below could
                settle without a fistfight.
              </p>
              <ul className="divide-y divide-rule border-t border-rule">
                {rhetoricals.map((m) => (
                  <li key={m.id} className="px-4 py-2.5">
                    <p className="text-[15px] leading-snug italic">
                      &ldquo;{m.description}&rdquo;
                    </p>
                  </li>
                ))}
              </ul>
              <p className="px-4 py-2 text-[12px] text-mute italic text-right">
                — the group chat, Tuesday
              </p>
            </>
          )}

          {book && <CopyText label="Copy the lines" body={oddsText(doc, book.odds)} />}

          {book && <BookPosition doc={doc} book={book} isAdmin={isAdmin} />}

          {isAdmin && (
            <>
              <ConfirmQueue
                doc={doc}
                bets={pending}
                markets={markets}
                busy={busy}
                onAct={(id, action) =>
                  run(
                    () => updateBet(id, action),
                    action === 'confirm'
                      ? 'Confirmed — that bet is live'
                      : action === 'void'
                        ? 'Voided — the stake goes back'
                        : 'Removed from the queue',
                  )
                }
              />
              <PostMarket
                doc={doc}
                busy={busy}
                onPost={(input) => run(() => postOdds(input), 'Line is up')}
              />
              <ClearBook
                busy={busy}
                onClear={(withMarkets) =>
                  run(
                    async () => {
                      const r = await clearBook(withMarkets)
                      return r
                    },
                    withMarkets ? 'Book cleared — slips and lines' : 'Slips cleared — lines still up',
                  )
                }
              />
            </>
          )}
        </>
      ) : (
        <MyBets
          doc={doc}
          bets={myBets}
          markets={markets}
          me={me}
          stake={stake}
          busy={busy}
          onBack={(id) =>
            run(
              () => placeBet(id, stake),
              `${formatMoney(stake)} down — send it to the bookie to make it live`,
            )
          }
        />
      )}
    </>
  )
}

/** One stake, applied to the whole board. See the header. */
/**
 * Turn what somebody types into cents, or nothing.
 *
 * Accepts "40", "$40", "40.50" and "1,000" because all four are what a person
 * actually types on a phone, and rejects the rest by returning null rather than
 * by guessing — a stake read wrong is money read wrong.
 */
export function parseStake(text: string): number | null {
  const cleaned = text.replace(/[$,\s]/g, '')
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) return null
  const cents = Math.round(Number(cleaned) * 100)
  return cents > 0 ? cents : null
}

function StakePicker({ stake, onStake }: { stake: number; onStake: (n: number) => void }) {
  const [custom, setCustom] = useState('')
  const typed = parseStake(custom)
  const onPreset = STAKES.includes(stake)

  return (
    <div className="px-4 py-3 border-b border-rule">
      <p className="bc-label text-mute mb-2">Quote the board at</p>
      {/*
        Presets and the free field on one row. Whatever the board is quoted at
        changes every price on it, so somebody comparing lines at $40 reads the
        whole card in the same money.
      */}
      <div className="flex gap-2">
        {STAKES.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => {
              setCustom('')
              onStake(s)
            }}
            className={`flex-1 bc-num text-sm py-2 bc-tap border border-ink ${
              stake === s ? 'bg-ink text-paper' : 'bg-paper'
            }`}
          >
            {formatMoney(s)}
          </button>
        ))}
        <input
          value={custom}
          onChange={(e) => {
            setCustom(e.target.value)
            const n = parseStake(e.target.value)
            if (n) onStake(n)
          }}
          inputMode="decimal"
          placeholder="Other"
          aria-label="Quote the board at a custom amount"
          className={`flex-1 min-w-0 border px-2 py-2 bc-num bc-tap ${
            !onPreset && custom !== '' && typed
              ? 'border-ink bg-ink text-paper'
              : 'border-ink bg-paper'
          }`}
        />
      </div>
      {/* Only says anything when it has something to say. */}
      {custom !== '' && (
        <p className="text-[12px] text-mute mt-1.5">
          {typed ? `Quoting the board at ${formatMoney(typed)}.` : 'Not an amount.'}
        </p>
      )}
    </div>
  )
}

/**
 * A stake for this bet only.
 *
 * Separate from the board's quote on purpose: changing the board changes how
 * every line reads, and somebody putting a hundred on one man does not want to
 * re-read the whole card in hundreds to do it.
 */
function CustomStake({
  busy,
  odds,
  onPlace,
}: {
  busy: boolean
  odds: number
  onPlace: (cents: number) => void
}) {
  const [text, setText] = useState('')
  const cents = parseStake(text)
  const tooBig = cents !== null && cents > 500_000

  return (
    <div className="mt-2">
      <div className="flex gap-2">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          inputMode="decimal"
          placeholder="Other amount"
          aria-label="Bet a custom amount"
          className="flex-1 border border-ink px-3 py-2 bc-num bc-tap bg-paper"
        />
        <button
          type="button"
          disabled={busy || cents === null || tooBig}
          onClick={() => {
            if (cents) {
              onPlace(cents)
              setText('')
            }
          }}
          className="bc-tap bc-label px-4 border-2 border-ink disabled:opacity-30"
        >
          Back
        </button>
      </div>
      {text !== '' && (
        <p className="text-[12px] text-mute mt-1">
          {cents === null
            ? 'Not an amount.'
            : tooBig
              ? 'Over the $5,000 limit on a single slip.'
              : `${formatMoney(cents)} to win ${formatMoney(profitCents(cents, odds))}.`}
        </p>
      )}
    </div>
  )
}

/**
 * One button, one block of text, into the clipboard.
 *
 * The share screen exists for the evening post; this is for the moment the
 * bookie is asked "what are the prices" in the thread and wants to answer
 * without screenshotting a phone.
 */
function CopyText({ label, body }: { label: string; body: string }) {
  const [done, setDone] = useState(false)
  return (
    <div className="px-4 py-3 border-b border-rule">
      <button
        type="button"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(body)
            setDone(true)
            setTimeout(() => setDone(false), 2000)
          } catch {
            // Some in-app browsers refuse the clipboard; the share screen has
            // the same text in a selectable box for exactly that case.
          }
        }}
        className="bc-tap w-full border-2 border-ink bc-label py-3"
      >
        {done ? 'Copied' : label}
      </button>
    </div>
  )
}

function MarketRow({
  doc,
  market,
  stake,
  busy,
  isAdmin,
  myBets,
  open,
  onToggle,
  onPlace,
  onMarket,
  onArchive,
}: {
  doc: Tournament
  market: OddsMarket
  stake: number
  busy: boolean
  isAdmin: boolean
  myBets: Bet[]
  open: boolean
  onToggle: () => void
  onPlace: () => void
  onArchive: () => void
  onMarket: (
    patch: {
      status?: OddsMarket['status']
      result?: MarketResult | null
      description?: string
      market?: string
    },
    ok: string,
  ) => void
}) {
  const live = market.status === 'open'
  /*
   * The wording, editable in place — Scott's ask after a hand-typed prop
   * went up reading "Springer to finish for first flight" with "Flight 1 -
   * springer" underneath it. A line the table cannot read is a line nobody
   * bets, and re-posting it would strand the slips already on it. Only the
   * words change; the price, the slips and the history are untouched.
   */
  const [editing, setEditing] = useState(false)
  const [draftName, setDraftName] = useState(market.description)
  const [draftKind, setDraftKind] = useState(market.market)
  const returns = payoutCents(stake, market.american_odds)

  const subject = market.subject_id ? playerName(doc, market.subject_id) : null
  const sub = [market.market, subject].filter(Boolean).join(' · ')

  return (
    <>
      {/*
        A prop is a SENTENCE, so it gets the whole width and the money goes
        underneath — Scott's call after "Springer to top the whole field in
        both Wednesday quota nines at Bandon Dunes" came out four lines tall
        in a column squeezed beside its own price. The field's outrights keep
        the name-left/price-right shape, because a name is two words.
      */}
      <button
        type="button"
        onClick={onToggle}
        className="bc-tap w-full text-left px-4 py-3 border-b border-rule"
      >
        <span className="bc-name text-sm leading-snug block">{market.description}</span>
        <span className="flex items-baseline justify-between gap-3 mt-1">
          <span className="text-[12px] text-mute min-w-0 truncate">
            {sub}
            {market.status === 'settled'
              ? ` · ${market.result ?? 'settled'}`
              : market.status === 'closed'
                ? ' · closed'
                : ''}
          </span>
          <span className="shrink-0 text-right whitespace-nowrap">
            <span className="bc-num text-base">{formatAmerican(market.american_odds)}</span>
            {/* Only quoted while the line is live — a return on a market
                nobody can bet is a promise the board cannot keep. */}
            {live && (
              <span className="text-[12px] text-mute"> · returns {formatMoney(returns)}</span>
            )}
          </span>
        </span>
      </button>

      {open && (
        <div className="px-4 py-3 border-b border-rule bg-wash">
          <p className="text-[13px] text-mute leading-snug mb-3">
            {formatAmerican(market.american_odds)} —{' '}
            {market.american_odds < 0
              ? `risk ${formatMoney(Math.abs(market.american_odds) * 100)} to win ${formatMoney(10000)}`
              : `${formatMoney(10000)} wins ${formatMoney(market.american_odds * 100)}`}
            . The book is calling it {Math.round(impliedProbability(market.american_odds) * 100)}%
            likely.
          </p>

          {live ? (
            <button
              type="button"
              disabled={busy}
              onClick={onPlace}
              className="w-full bg-ink text-paper bc-label py-3 bc-tap disabled:opacity-50"
            >
              Put {formatMoney(stake)} on it — {formatMoney(returns)} back
            </button>
          ) : (
            <p className="bc-label text-mute">
              {market.status === 'settled'
                ? `Settled ${market.result}`
                : 'Closed — no more bets'}
            </p>
          )}

          {live && (
            <p className="text-[12px] text-mute mt-2 leading-snug">
              {formatMoney(stake)} stake, {formatMoney(profitCents(stake, market.american_odds))}{' '}
              profit. Nothing is live until the bookie has your Venmo.
            </p>
          )}

          {myBets.length > 0 && (
            <p className="text-[12px] text-mute mt-2">
              You have {myBets.length === 1 ? 'a bet' : `${myBets.length} bets`} on this:{' '}
              {myBets
                .map(
                  (b) =>
                    `${formatMoney(b.stake_cents)} at ${formatAmerican(b.odds_at_placement)} (${STATUS_LABEL[b.status]})`,
                )
                .join(', ')}
              .
            </p>
          )}

          {isAdmin && (
            <div className="mt-3 pt-3 border-t border-rule">
              <p className="bc-label text-mute mb-2">Bookie</p>
              <div className="flex flex-wrap gap-2">
                {market.status !== 'open' && (
                  <BookieButton
                    busy={busy}
                    onClick={() => onMarket({ status: 'open', result: null }, 'Line reopened')}
                  >
                    Reopen
                  </BookieButton>
                )}
                {market.status === 'open' && (
                  <BookieButton
                    busy={busy}
                    onClick={() => onMarket({ status: 'closed' }, 'No more bets')}
                  >
                    Close the line
                  </BookieButton>
                )}
                {(['win', 'lose', 'void'] as const).map((r) => (
                  <BookieButton
                    key={r}
                    busy={busy}
                    strong={market.status === 'settled' && market.result === r}
                    onClick={() =>
                      onMarket(
                        { status: 'settled', result: r },
                        r === 'void' ? 'Voided — stakes go back' : `Settled ${r}`,
                      )
                    }
                  >
                    Settle {r}
                  </BookieButton>
                ))}
                {/*
                  Archive, not delete. The row stays and the audit trail with
                  it; it just leaves the board. For the line posted twice or
                  typed wrong — the server refuses while any slip on it is
                  still owed an answer.
                */}
                <BookieButton busy={busy} onClick={onArchive}>
                  Archive
                </BookieButton>
                <BookieButton
                  busy={busy}
                  onClick={() => {
                    setDraftName(market.description)
                    setDraftKind(market.market)
                    setEditing((v) => !v)
                  }}
                >
                  {editing ? 'Cancel' : 'Edit wording'}
                </BookieButton>
              </div>

              {editing && (
                <div className="grid gap-2 mt-3">
                  <label className="grid gap-1">
                    <span className="bc-label text-mute">What it says on the board</span>
                    <input
                      value={draftName}
                      onChange={(e) => setDraftName(e.target.value)}
                      placeholder="Springer to make Flight 1"
                      className="border-2 border-ink px-3 py-2 text-sm bc-tap bg-paper"
                    />
                  </label>
                  <label className="grid gap-1">
                    <span className="bc-label text-mute">The small line under it</span>
                    <input
                      value={draftKind}
                      onChange={(e) => setDraftKind(e.target.value)}
                      placeholder="Flight 1"
                      className="border-2 border-ink px-3 py-2 text-sm bc-tap bg-paper"
                    />
                  </label>
                  <div className="flex gap-2">
                    <BookieButton
                      busy={busy || draftName.trim().length === 0}
                      strong
                      onClick={() => {
                        onMarket(
                          { description: draftName.trim(), market: draftKind.trim() || market.market },
                          'Wording updated',
                        )
                        setEditing(false)
                      }}
                    >
                      Save wording
                    </BookieButton>
                  </div>
                  <p className="text-[12px] text-mute leading-snug">
                    Words only — the price stays where it is and every slip already on this
                    line keeps the odds it was struck at.
                  </p>
                </div>
              )}
              {/* Settling is reversible and says so, because the alternative is a
                  bookie who will not touch the button until he is certain, and
                  the board sits unsettled all week. */}
              <p className="text-[12px] text-mute mt-2 leading-snug">
                Settling pays every confirmed bet at the price it was taken.
                Unconfirmed ones are voided — the money never arrived. Reopening
                undoes all of it. Archiving takes the market and its settled
                slips off the board together; nothing is deleted, and it is
                refused while any slip is still owed an answer.
              </p>
            </div>
          )}
        </div>
      )}
    </>
  )
}

function BookieButton({
  children,
  busy,
  strong,
  onClick,
}: {
  children: React.ReactNode
  busy: boolean
  strong?: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      disabled={busy}
      onClick={onClick}
      className={`bc-label px-3 py-2 bc-tap border border-ink disabled:opacity-50 ${
        strong ? 'bg-signal text-paper' : 'bg-paper'
      }`}
    >
      {children}
    </button>
  )
}

/**
 * The bookie's inbox.
 *
 * Placed high and never collapsed, because this queue is the only thing
 * standing between a claimed bet and a real one, and a bet left pending
 * through the settlement of its market is silently voided. Somebody has to look
 * at it.
 */
function ConfirmQueue({
  doc,
  bets,
  markets,
  busy,
  onAct,
}: {
  doc: Tournament
  bets: Bet[]
  markets: OddsMarket[]
  busy: boolean
  onAct: (id: string, action: 'confirm' | 'void' | 'archive') => void
}) {
  const total = bets.reduce((sum, b) => sum + b.stake_cents, 0)

  /*
   * By man, not by slip — Scott's ask, and the bookie's actual job.
   *
   * Money arrives as ONE Venmo covering everything a man owes, so a queue
   * listing eight $25 slips separately made Springer add them up on his
   * thumb and then confirm them one at a time. Grouped, the header is the
   * number to compare against the notification, and "All landed" clears the
   * man in a tap. Biggest debt first: that is the one worth chasing.
   */
  const byMan = [...new Set(bets.map((b) => b.player_id))]
    .map((pid) => ({
      pid,
      slips: bets.filter((b) => b.player_id === pid),
      owed: bets.filter((b) => b.player_id === pid).reduce((n, b) => n + b.stake_cents, 0),
    }))
    .sort((a, b) => b.owed - a.owed)

  return (
    <>
      <SectionRule>
        Waiting on Venmo{bets.length > 0 ? ` — ${formatMoney(total)}` : ''}
      </SectionRule>
      {bets.length === 0 ? (
        <Empty>Nothing outstanding.</Empty>
      ) : (
        byMan.map((man) => (
          <div key={man.pid} className="border-b-2 border-ink">
            <div className="px-4 py-2 bg-wash flex items-baseline justify-between gap-3">
              <span className="min-w-0">
                <span className="bc-name text-sm">{playerName(doc, man.pid)}</span>
                <span className="text-[12px] text-mute">
                  {' '}· {man.slips.length} slip{man.slips.length === 1 ? '' : 's'}
                </span>
              </span>
              <span className="flex items-baseline gap-3 shrink-0">
                <span className="bc-num text-base">{formatMoney(man.owed)}</span>
                {man.slips.length > 1 && (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => man.slips.forEach((b) => onAct(b.id, 'confirm'))}
                    className="bc-label bc-tap border-2 border-ink px-2.5 py-1 disabled:opacity-50"
                  >
                    All landed
                  </button>
                )}
              </span>
            </div>
            {man.slips.map((b) => {
          const market = markets.find((m) => m.id === b.odds_id)
          return (
            <div key={b.id} className="border-b border-rule last:border-b-0">
              <Row
                name={market?.description ?? 'Market withdrawn'}
                wrap
                right={
                  <Chip
                    chip={{
                      primary: formatMoney(b.stake_cents),
                      secondary: formatAmerican(b.odds_at_placement),
                      tone: 'neutral',
                    }}
                  />
                }
              />
              <div className="flex gap-2 px-4 pb-3">
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => onAct(b.id, 'confirm')}
                  className="flex-1 bg-ink text-paper bc-label py-2.5 bc-tap disabled:opacity-50"
                >
                  Money landed
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => onAct(b.id, 'void')}
                  className="bc-label px-3 py-2.5 bc-tap border border-ink disabled:opacity-50"
                >
                  Void
                </button>
                {/*
                  Void refunds; archive removes. A slip nobody ever sent money
                  for does not want a refund, it wants to stop cluttering the
                  queue — and the row survives either way.
                */}
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => onAct(b.id, 'archive')}
                  className="bc-label px-3 py-2.5 bc-tap border border-rule text-mute disabled:opacity-50"
                >
                  Remove
                </button>
              </div>
            </div>
          )
            })}
          </div>
        ))
      )}
    </>
  )
}

function PostMarket({
  doc,
  busy,
  onPost,
}: {
  doc: Tournament
  busy: boolean
  onPost: (input: {
    market: string
    description: string
    americanOdds: number
    subjectId?: string | null
  }) => void
}) {
  const [market, setMarket] = useState('Championship')
  const [description, setDescription] = useState('')
  const [odds, setOdds] = useState('-150')
  const [subjectId, setSubjectId] = useState('')

  const parsed = Number(odds)
  // Mirrors the server's rule rather than trusting it to explain itself after a
  // round trip. The server still checks; this just stops the trip.
  const valid =
    description.trim().length > 0 &&
    market.trim().length > 0 &&
    Number.isInteger(parsed) &&
    Math.abs(parsed) >= 100

  return (
    <>
      <SectionRule>Post a line</SectionRule>
      <div className="px-4 py-3 grid gap-2">
        <input
          value={market}
          onChange={(e) => setMarket(e.target.value)}
          placeholder="Market, e.g. Championship"
          className="border border-ink px-2 py-2 bc-name text-sm bc-tap bg-paper"
        />
        <input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Blackham to win the Championship"
          className="border border-ink px-2 py-2 text-sm bc-tap bg-paper"
        />
        <div className="flex gap-2">
          <input
            value={odds}
            onChange={(e) => setOdds(e.target.value)}
            inputMode="numeric"
            placeholder="-150"
            className="w-24 border border-ink px-2 py-2 bc-num text-sm bc-tap bg-paper"
          />
          <select
            value={subjectId}
            onChange={(e) => setSubjectId(e.target.value)}
            className="flex-1 border border-ink px-2 py-2 bc-label bc-tap bg-paper"
          >
            <option value="">No subject</option>
            {doc.players
              .filter((p) => p.active)
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.displayName}
                </option>
              ))}
          </select>
        </div>

        {valid && (
          <p className="text-[12px] text-mute leading-snug">
            {formatAmerican(parsed)} — {formatMoney(2500)} returns{' '}
            {formatMoney(payoutCents(2500, parsed))}. That is the book calling it{' '}
            {Math.round(impliedProbability(parsed) * 100)}% likely.
          </p>
        )}

        <button
          type="button"
          disabled={busy || !valid}
          onClick={() => {
            onPost({
              market: market.trim(),
              description: description.trim(),
              americanOdds: parsed,
              subjectId: subjectId || null,
            })
            setDescription('')
          }}
          className="bg-ink text-paper bc-label py-3 bc-tap disabled:opacity-50"
        >
          Put it up
        </button>
        <p className="text-[12px] text-mute leading-snug">
          American odds. Negative is the favorite — -150 means risk $150 to win
          $100. Positive is the longshot — +400 means $100 wins $400. Nothing
          between -100 and +100 exists.
        </p>
      </div>
    </>
  )
}

/** A player's own slips, and where the week stands for them. */
/**
 * Wipe the book while testing.
 *
 * Two taps, because it refunds every confirmed slip and takes them all off the
 * board in one go. Named for what it does rather than what it is for: this is
 * the control somebody reaches for at the end of a rehearsal, and the label has
 * to be unambiguous to whoever finds it mid-week by accident.
 */
function ClearBook({
  busy,
  onClear,
}: {
  busy: boolean
  onClear: (withMarkets: boolean) => void
}) {
  const [armed, setArmed] = useState<null | 'slips' | 'all'>(null)

  const button = (which: 'slips' | 'all', label: string) => (
    <button
      type="button"
      disabled={busy}
      onClick={() => {
        if (armed === which) {
          onClear(which === 'all')
          setArmed(null)
        } else {
          setArmed(which)
        }
      }}
      onBlur={() => armed === which && setArmed(null)}
      className={`flex-1 bc-tap bc-label py-2.5 ${
        armed === which ? 'bg-signal text-paper' : 'border-2 border-ink'
      }`}
    >
      {armed === which ? 'Tap again' : label}
    </button>
  )

  return (
    <>
      <SectionRule>Clear the book</SectionRule>
      <p className="px-4 pt-1 pb-2 text-[13px] text-mute leading-snug">
        Voids every slip — refunding anything already collected — and takes them off
        the board. For clearing up after a rehearsal. Nothing is deleted; the rows
        keep their history and stop appearing.
      </p>
      <div className="flex gap-2 px-4 pb-6">
        {button('slips', 'Clear slips')}
        {button('all', 'Slips and lines')}
      </div>
    </>
  )
}

function MyBets({
  doc,
  bets,
  markets,
  me,
  stake,
  busy,
  onBack,
}: {
  doc: Tournament
  bets: Bet[]
  markets: OddsMarket[]
  me: string
  stake: number
  busy: boolean
  onBack: (oddsId: string) => void
}) {
  /*
   * The tickets a man built and then walked away from.
   *
   * Ten of the fourteen parlays on this board have no money on them —
   * built on the plane, priced, admired, forgotten. The builder is the one
   * person who wanted each of them, so he is the one worth reminding, and
   * this is the screen he opens to see his own action. Only OPEN markets he
   * posted with no slip of his own; backing one is a tap at the board's
   * stake.
   */
  const unbacked = markets.filter(
    (m) =>
      m.posted_by === me &&
      m.status === 'open' &&
      !bets.some((b) => b.odds_id === m.id),
  )

  if (bets.length === 0 && unbacked.length === 0) {
    return <Empty>You have not backed anything yet.</Empty>
  }

  /*
   * Staked counts slips the bookie actually confirmed, not slips that exist.
   * `confirmed_at` is the record that the Venmo landed and it survives
   * settlement — the same test the server settles on — so a bet that was voided
   * while still pending correctly contributes nothing to either column rather
   * than showing up as a loss of money that never left the account.
   */
  const staked = bets
    .filter((b) => b.confirmed_at !== null)
    .reduce((sum, b) => sum + b.stake_cents, 0)
  const returned = bets.reduce((sum, b) => sum + (b.payout_cents ?? 0), 0)
  const settled = bets.some((b) => b.payout_cents !== null)

  return (
    <>
      {unbacked.length > 0 && (
        <>
          <SectionRule>
            You built {unbacked.length === 1 ? 'this' : `these ${unbacked.length}`} — no money on{' '}
            {unbacked.length === 1 ? 'it' : 'them'}
          </SectionRule>
          <ul className="divide-y divide-rule border-t border-rule">
            {unbacked.map((m) => (
              <li key={m.id} className="px-4 py-2.5">
                <p className="bc-name text-sm leading-snug">{m.description}</p>
                <div className="flex items-baseline justify-between gap-3 mt-1.5">
                  <span className="text-[13px]">
                    <span className="bc-num text-base">{formatAmerican(m.american_odds)}</span>
                    <span className="text-mute">
                      {' '}· {formatMoney(stake)} returns{' '}
                      {formatMoney(stake + payoutCents(stake, m.american_odds))}
                    </span>
                  </span>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => onBack(m.id)}
                    className="bc-tap bc-label border-2 border-ink px-3 py-1.5 shrink-0 disabled:opacity-50"
                  >
                    Back it
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}

      {bets.length > 0 && <SectionRule>Your slips</SectionRule>}
      {bets.map((b) => {
        const market = markets.find((m) => m.id === b.odds_id)
        const decided = b.payout_cents !== null
        return (
          <Row
            key={b.id}
            wrap
            name={market?.description ?? 'Market withdrawn'}
            sub={`${formatMoney(b.stake_cents)} at ${formatAmerican(b.odds_at_placement)}${
              b.confirmed_by ? ` · confirmed by ${playerName(doc, b.confirmed_by)}` : ''
            }`}
            tag={STATUS_LABEL[b.status]}
            // Only a settled slip gets the leader treatment, and only a winning
            // one — inverting a pending bet would read as "this is money".
            lead={b.status === 'won'}
            right={
              <Chip
                chip={{
                  primary: decided
                    ? formatMoney(b.payout_cents ?? 0)
                    : formatMoney(payoutCents(b.stake_cents, b.odds_at_placement)),
                  secondary: decided ? 'paid' : 'to return',
                  tone: 'neutral',
                }}
              />
            }
          />
        )
      })}

      {settled && (
        <div className="px-4 py-3 border-t border-rule">
          <div className="flex items-baseline justify-between">
            <span className="bc-label text-mute">Staked</span>
            <span className="bc-num">{formatMoney(staked)}</span>
          </div>
          <div className="flex items-baseline justify-between mt-1">
            <span className="bc-label text-mute">Returned</span>
            <span className={`bc-num ${returned < staked ? 'text-signal' : ''}`}>
              {formatMoney(returned)}
            </span>
          </div>
          <p className="text-[12px] text-mute leading-snug mt-1.5">
            Counts settled and voided slips only. Anything still live is not in
            these numbers, because it has not happened yet.
          </p>
        </div>
      )}
    </>
  )
}
